import React, { useEffect, useState } from 'react';
import { navigate, Redirect, useLocation } from '@reach/router';
import Cookies from 'universal-cookie/es6';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import {
  autoSignIn,
  selectAuthMessage,
  selectSignedIn,
  showApp,
  userSignOut,
} from 'reducers/auth';
import { AppPath, NO_ACCESS_MESSAGE, NOT_FOUND_PAGE } from 'appConstants';
import { MFA_CHALLENGE_AREA } from 'helpers/mfa/mfaReturnUrl';
import { subjectSiteLaunchUrl } from 'helpers/subjectSites';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import Private from 'components/private/Private';
import { hasPageAccess } from 'helpers/hasPageAccess';
import Footer from './Footer';
import Header from './Header';
import { DashboardApi } from '../../pages/dashboard/dashboardApi';
import { Spinner } from 'components/spinner/Spinner';

type PageProps = {
  isPublic?: boolean;
  isNotFound?: boolean;
  className?: string;
  redirectPath?: string;
  redirectParams?: boolean;
  autoLogin?: boolean;
  iFramePage?: boolean;
  bypassPageAccess?: boolean;
  children?: any;
};

const cookies = new Cookies();

const Page: React.FC<PageProps> = ({
  isPublic = false,
  isNotFound = false,
  className,
  redirectPath,
  children,
  redirectParams = true,
  autoLogin = true,
  iFramePage = false,
  bypassPageAccess = false,
}) => {
  const [token, setToken] = useState<string>('');
  const [showPleaseWait, setShowPleaseWait] = useState<boolean>(false);
  const dispatch = useDispatch();
  const isSignedIn = useSelector(selectSignedIn);
  const location = useLocation();
  const pathName = location.pathname;

  const message = useSelector(selectAuthMessage);

  const getFetchAutoLoginToken = async () => {
    const result = await DashboardApi.getAutoLoginToken();
    if (result.data) {
      setToken(result.data.token);
    }
    if (result.error) {
      setToken('');
    }
  };

  const queryParams: any = location
    ? queryString.parse(location.search)
    : undefined;

  useEffect(() => {
    if(isSignedIn){
      getFetchAutoLoginToken();
    }
  }, []);    

  useEffect(() => {
    // On an MFA arrival redirectURL is where to go after verifying, not a subject site to launch now.
    // area can arrive twice, because the sign-in redirect keeps the original query string.
    const isMfaArrival = ([] as string[]).concat(queryParams?.area ?? []).includes(MFA_CHALLENGE_AREA);
    if (isSignedIn && token && queryParams.redirectURL && !isMfaArrival) {
      const launchUrl = subjectSiteLaunchUrl(queryParams.redirectURL, token, queryParams.area);
      if (!launchUrl) return;
      setShowPleaseWait(true);
      window.location.href = launchUrl;
    }
  }, [token]);

  useEffect(() => {
    if (queryParams?.auth === 'sign-out' && isSignedIn) {
      dispatch(userSignOut())
    };
  }, []);


  if (queryParams?.auth === 'sign-out' && isSignedIn) return null;

  // const getURLToken = new URLSearchParams(window.location.search).get('token');
  // if (getURLToken && autoLogin) {
  //   dispatch(autoSignIn({ token: encodeURIComponent(getURLToken) }));

  //   if (message) {
  //     navigate(AppPath.SIGN_IN);
  //   }
  //   return (
  //     <div className="PageSpinner">
  //       <Spinner />
  //     </div>
  //   );
  // }

  if (isSignedIn && isPublic) {
    return (
      <Redirect
        noThrow
        to={
          redirectPath
            ? `/${redirectPath}${redirectParams ? '?area=&token=' : ''}`
            : AppPath.DASHBOARD
        }
      />
    );
  }

  return isPublic ? (
    <div className={`Page ${className ? className : ''}`}>
      {!iFramePage && <Header />}
      <main className="Main">{children}</main>
      {!iFramePage && <Footer />}
    </div>
  ) : (
    <Private>
      <div className={`Page ${className ? className : ''}`}>
        {!iFramePage && <Header />}
        <main className={"Main" + (iFramePage ? " iFramePage" : "")}>
          {bypassPageAccess || hasPageAccess(pathName) || pathName === "/school/documents/mydocuments" ? (
            children
          ) : (
            <>
              {showPleaseWait ? 
                <div className="PleaseWaitMsg">
                  <div className="MsgContainer">
                    <Spinner />
                    <div className="Txt">Please wait</div>
                  </div>
                </div>
                :
                <ErrorMessage
                  message={isNotFound ? NOT_FOUND_PAGE : NO_ACCESS_MESSAGE}
                />
              }
            </>
          )}
        </main>
        {!iFramePage && <Footer />}
      </div>
    </Private>
  );
};

export default Page;
